/* eslint-disable react-native/no-inline-styles */
import React from 'react';
import { View, Text, ActivityIndicator, ScrollView } from 'react-native';
import i18n from 'i18n-js';

import { scale, fullWidth } from '@components/frame';
import { SCREEN_PADDING_HORIZONTAL, isPhoneNative, localTime } from '@helpers';
import ColorWrapper from '@styles/colors';
import { font } from '@styles/fonts';
import { getBoxShadowStyle } from '@styles/shadow';
import Line from '@components/common/line';
import TouchableElement from '@components/common/touchableElement';
import EditField from '@components/common/editField';
import Loading from '@components/loading';
import Header from '@components/settings/v2/common/SettingsHeader';

import {
  modalBg,
  SCREEN_BORDER_RADIUS,
  SCREEN_PADDING_HORIZONTAL_FOR_PAGE,
  SCREEN_PADDING_VERTICAL_FOR_ITEM,
  TEST_IDS,
} from '../../../../../v2/pages/common/constants/base.constants';
import ModalWrapper from '@components/modal/ModalWrapper';
import SelectableList from '@components/devInfo/environment/components/SelectableList';

import { useManageUserDetails } from './hooks/useManageUserDetails';

const { userDetailsScreen } = TEST_IDS;

const UserDetails = ({ C, route, rootStack }) => {
  const {
    userData,
    userFields,
    userProfile,
    devices,
    isRegistered,
    showActions,
    showEditUserName,
    toggleEditUserName,
    showEditRole,
    setShowEditRole,
    deleting,
    changingAccess,
    triggerBlueButton,
    triggerDelete,
    editNameConfig,
    roleItems,
    updateUserRole,
    navigateToDeviceDetails,
  } = useManageUserDetails({ route, rootStack });

  const shadowStyles = getBoxShadowStyle({ type: 'Field', C });

  return [
    <View
      key="UserDetails"
      style={{
        height: '100%',
        width: '100%',
        backgroundColor: C('Background/Light/Detail'),
        alignItems: 'center',
      }}
    >
      <Header name={'UserDetails'} key="UserDetailsHeader" backBtnText="Users" />
      <ScrollView
        style={{ width: '100%', flex: 1 }}
        contentContainerStyle={{ paddingBottom: scale(50), paddingTop: scale(SCREEN_PADDING_VERTICAL_FOR_ITEM) }}
      >
        <View>
          {userData.emailAddress ? (
            <View
              testID={userDetailsScreen.content}
              style={{
                width: '100%',
              }}
            >
              <View style={{ paddingHorizontal: scale(SCREEN_PADDING_HORIZONTAL) }}>
                <View style={[shadowStyles, { borderRadius: scale(SCREEN_BORDER_RADIUS), width: '100%' }]}>
                  {Object.keys(userFields).map((item, index) => (
                    <Line
                      {...{
                        item: userFields[item],
                        index,
                        array: Object.keys(userFields),
                        C,
                        roundTop: index === 0,
                        roundBottom: Object.keys(userFields).length - 1 === index,
                      }}
                      key={item}
                    />
                  ))}
                </View>
              </View>
              {showActions && (
                <View style={{ paddingHorizontal: scale(SCREEN_PADDING_HORIZONTAL_FOR_PAGE) }}>
                  {!isRegistered && (
                    <View style={{ paddingHorizontal: scale(15), marginTop: scale(28) }}>
                      <Text style={[font['Footnote'], { color: C('Label/Light/Tertiary') }]}>
                        {i18n.t('INVITATION SENT ON')} {localTime(userData.invitedOn).format('DD')}-
                        {i18n.t(localTime(userData.invitedOn).format('MMM')).toLocaleUpperCase()}-{localTime(userData.invitedOn).format('YYYY')}
                      </Text>
                    </View>
                  )}
                  <View
                    testID={userDetailsScreen.blockButton}
                    style={[shadowStyles, { height: scale(42), marginTop: scale(isRegistered ? 16 : 5), borderRadius: scale(SCREEN_BORDER_RADIUS) }]}
                  >
                    <TouchableElement onPress={triggerBlueButton}>
                      {({ boxStyle, pressHoverColor }) => (
                        <View
                          style={[
                            {
                              backgroundColor: C(pressHoverColor('Background / Light / Field')),
                              borderRadius: scale(SCREEN_BORDER_RADIUS),
                              height: 44,
                              width: '100%',
                              justifyContent: 'center',
                              alignContent: 'center',
                            },
                            boxStyle,
                          ]}
                        >
                          {changingAccess ? (
                            <ActivityIndicator size="small" color="#777777" />
                          ) : (
                            <Text
                              style={[
                                {
                                  color: C(pressHoverColor('Link/Light/Primary')),
                                  textAlign: 'center',
                                },
                                font.Body,
                              ]}
                            >
                              {isRegistered ? i18n.t(userData.isBlocked ? 'Unblock user' : 'Block user') : i18n.t('Re-Send Invitation')}
                            </Text>
                          )}
                        </View>
                      )}
                    </TouchableElement>
                  </View>
                  <View
                    testID={userDetailsScreen.deleteButton}
                    style={[shadowStyles, { height: scale(42), marginTop: scale(20), borderRadius: scale(SCREEN_BORDER_RADIUS) }]}
                  >
                    <TouchableElement
                      onPress={() => {
                        if (deleting) {
                          return;
                        }
                        triggerDelete();
                      }}
                    >
                      {({ boxStyle, pressHoverColor }) => (
                        <View
                          style={[
                            {
                              backgroundColor: C(pressHoverColor('Background / Light / Field')),
                              borderRadius: scale(SCREEN_BORDER_RADIUS),
                              height: 44,
                              width: '100%',
                              justifyContent: 'center',
                              alignContent: 'center',
                            },
                            boxStyle,
                          ]}
                        >
                          {deleting ? (
                            <ActivityIndicator size="small" color="#777777" />
                          ) : (
                            <Text
                              style={[
                                {
                                  color: C(pressHoverColor('Label / Light / Error')),
                                  textAlign: 'center',
                                },
                                font.Body,
                              ]}
                            >
                              {i18n.t(isRegistered ? 'Delete user' : 'Remove From Invited List')}
                            </Text>
                          )}
                        </View>
                      )}
                    </TouchableElement>
                  </View>

                  {!!devices.length && (
                    <View testID={userDetailsScreen.devicesSection}>
                      <View style={{ width: '100%', paddingHorizontal: scale(15), marginTop: scale(28), marginBottom: scale(5) }}>
                        <Text style={[font['Footnote'], { color: C('Label/Light/Tertiary'), textTransform: 'uppercase' }]}>
                          {i18n.t('DEVICES OF')} {`${userProfile.firstName} ${userProfile.lastName}`}
                        </Text>
                      </View>
                      <View style={[shadowStyles, { borderRadius: scale(SCREEN_BORDER_RADIUS) }]}>
                        {devices.map((item, index) => (
                          <Line
                            {...{
                              item: {
                                ...item,
                                onClick: () => navigateToDeviceDetails(item.deviceId),
                              },
                              index,
                              array: devices,
                              C,
                              roundTop: index === 0,
                              roundBottom: devices.length - 1 === index,
                            }}
                            key={item.deviceId}
                          />
                        ))}
                      </View>
                    </View>
                  )}
                </View>
              )}
            </View>
          ) : (
            <Loading background={C('Background/Light/Detail')} />
          )}
        </View>
      </ScrollView>
    </View>,
    <EditField
      key="EditName"
      {...{
        C,
        showModal: showEditUserName,
        onClose: toggleEditUserName,
        ...editNameConfig,
      }}
    />,
    showEditRole && (
      <ModalWrapper backdropColor="Overlay/Light/Dim strong" C={C} onBackdropPress={() => setShowEditRole(false)} isVisible={showEditRole}>
        <View style={{ width: '100%', alignItems: 'center', justifyContent: 'center' }}>
          <View
            style={{
              width: isPhoneNative ? fullWidth - scale(SCREEN_PADDING_HORIZONTAL) * 2 : scale(700),
              backgroundColor: C(modalBg),
              borderRadius: scale(SCREEN_BORDER_RADIUS),
            }}
          >
            <SelectableList
              items={roleItems}
              selectedValue={userData.role}
              onSelect={updateUserRole}
              C={C}
            />
          </View>
        </View>
      </ModalWrapper>
    ),
  ];
};

export default ColorWrapper(UserDetails);
